import { useQuery } from "@apollo/client";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { sortBy, inSequence } from "Comparable";
import { BulmaButton } from "Components/Button";
import { ButtonIcon } from "Components/Icon";
import { LcpUserPageWrapper } from "Components/MainPageWrapper";
import { Section } from "Components/Section";
import { tableClassNames, TableHeading } from "Components/TableHelpers";
import { adminUsersQuery, AdminUsersQuery } from "generated/graphql";
import { Checkbox } from "Inputs/Checkbox";
import { createFieldProps } from "Inputs/FormTools";
import React, { useState } from "react";
import { getFullName } from "Tools";
import * as uuid from "uuid";
import { DashboardLink } from "Views/DashboardLink";
import { AddOrEditAccountModal } from "./AddOrEditAccountModal";

export function ManageAccountsPage() {
  const [modalProps, setModalProps] = useState<{
    isVisible: boolean;
    userToEdit?: AdminUsersQuery["adminUsers"][number];
  }>({ isVisible: false });
  const [showInactive, setShowInactive] = useState(false);
  const { data } = useQuery(adminUsersQuery);
  if (data === undefined) {
    return (
      <LcpUserPageWrapper documentTitle="Manage Admin Accounts">
        Loading..
      </LcpUserPageWrapper>
    );
  }
  const sortedAdminUsers = data.adminUsers
    .filter(u => (u.isActive || showInactive) && !u.isDevAccount)
    .toSorted(
      sortBy(e => inSequence(e.isActive ? 0 : 1, e.lastName.toLowerCase())),
    );
  return (
    <LcpUserPageWrapper
      documentTitle="Manage Admin Accounts"
      theme={{ mainDivStyle: { maxWidth: "720px" } }}
    >
      <Section title="Manage Admin Accounts" className="narrowFieldBodies2">
        <div className="has-text-centered" style={{ marginBottom: "1em" }}>
          <BulmaButton
            className="is-primary"
            onClick={() => setModalProps({ isVisible: true })}
          >
            <ButtonIcon icon={faPlus} />
            <span>New Admin Account</span>
          </BulmaButton>
        </div>
        <table className={tableClassNames}>
          <tbody>
            <tr>
              <TableHeading>Name</TableHeading>
              <TableHeading className="is-hidden-mobile">
                Email Address
              </TableHeading>
              <TableHeading>Status</TableHeading>
            </tr>
            {sortedAdminUsers.map(user => (
              <UserInfoRow
                key={user.id}
                user={user}
                onClick={() =>
                  setModalProps({
                    isVisible: true,
                    userToEdit: user,
                  })
                }
              />
            ))}
          </tbody>
        </table>
        <p style={{ textAlign: "center", marginBottom: 30 }}>
          <Checkbox
            field={createFieldProps({
              name: "showInactive",
              value: showInactive,
              setValue: setShowInactive,
            })}
          >
            Show inactive accounts
          </Checkbox>
        </p>
        <DashboardLink />
      </Section>
      <AddOrEditAccountModal
        {...modalProps}
        key={modalProps.userToEdit ? modalProps.userToEdit.id : uuid.v4()}
        onClose={() => setModalProps({ isVisible: false })}
      />
    </LcpUserPageWrapper>
  );
}

function UserInfoRow(props: {
  user: AdminUsersQuery["adminUsers"][number];
  onClick: () => void;
}) {
  return (
    <tr onClick={props.onClick} style={{ cursor: "pointer" }}>
      <td>{getFullName(props.user)}</td>
      <td className="is-hidden-mobile">{props.user.email}</td>
      <td>{props.user.isActive ? "Active" : "Inactive"}</td>
    </tr>
  );
}
